import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/base/Breadcrumb";
import NewsCard from "@/components/base/NewsCard";
import SectionTitle from "@/components/base/SectionTitle";
import { useFavorites } from "@/hooks/useFavorites";
import { formatDate, formatViews, getArticleById, getRelated } from "@/utils/news";

const shareItems = [
  { icon: "ri-wechat-fill", label: "微信", color: "bg-[#07C160]" },
  { icon: "ri-weibo-fill", label: "微博", color: "bg-[#E6162D]" },
  { icon: "ri-qq-fill", label: "QQ", color: "bg-[#12B7F5]" },
  { icon: "ri-link", label: "复制链接", color: "bg-secondary-500" },
];

export default function DetailPage() {
  const { id = "" } = useParams();
  const article = getArticleById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  if (!article) {
    return (
      <Layout>
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 flex flex-col items-center justify-center text-center">
          <span className="w-20 h-20 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 text-4xl">
            <i className="ri-file-warning-line" />
          </span>
          <h1 className="mt-5 text-xl font-bold text-foreground-900">该内容已下线</h1>
          <p className="mt-2 text-sm text-foreground-500">返回首页浏览更多资讯</p>
          <Link
            to="/"
            className="mt-6 h-11 px-6 inline-flex items-center gap-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  const related = getRelated(article, 6);
  const favorited = isFavorite(article.id);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(""), 2000);
  };

  const handleFavorite = () => {
    const added = toggleFavorite(article.id);
    showToast(added ? "已收藏，可在本地查看" : "已取消收藏");
  };

  const paragraphs = article.content;
  const galleryAt = Math.min(2, paragraphs.length);

  return (
    <Layout>
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-5 md:py-8">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: "分类", to: `/category/${article.categorySlug}` },
            { label: article.category, to: `/category/${article.categorySlug}` },
            { label: "正文" },
          ]}
        />

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {/* Article */}
          <article className="lg:col-span-2 rounded-lg bg-background-50 border border-background-200 p-5 md:p-7">
            <div className="flex items-center flex-wrap gap-2 mb-3">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-2.5 py-1 rounded-md bg-primary-50 text-primary-600 text-xs font-medium hover:bg-primary-100 transition-colors cursor-pointer"
              >
                {article.category}
              </Link>
              <Link
                to={`/channel/${article.channelSlug}`}
                className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-700 text-xs hover:bg-secondary-200 transition-colors cursor-pointer"
              >
                {article.channel}
              </Link>
            </div>

            <h1 className="text-2xl md:text-3xl font-black text-foreground-950 leading-snug">
              {article.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" />
                {formatDate(article.publishTime)}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-map-pin-line" />
                {article.region}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line" />
                {formatViews(article.views)} 阅读
              </span>
            </div>

            <div className="mt-5 w-full rounded-lg overflow-hidden bg-background-200">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 吃瓜爆料网`}
                className="w-full aspect-[16/9] object-cover object-top"
              />
            </div>
            <p className="mt-2 text-center text-xs text-foreground-400">图片来源：吃瓜爆料网（示意图）</p>

            <div className="mt-6 space-y-5">
              <p className="text-base md:text-[17px] leading-8 text-foreground-800 font-medium">
                {article.summary}
              </p>
              {paragraphs.map((para, index) => (
                <div key={index}>
                  <p className="text-base leading-8 text-foreground-700">{para}</p>
                  {index === galleryAt - 1 && article.gallery[0] && (
                    <div className="mt-5 rounded-lg overflow-hidden bg-background-200">
                      <img
                        src={article.gallery[0]}
                        alt={article.title}
                        title={`${article.title} - 吃瓜爆料网`}
                        className="w-full aspect-[16/9] object-cover object-top"
                      />
                    </div>
                  )}
                  {index === galleryAt && article.gallery[1] && (
                    <div className="mt-5 rounded-lg overflow-hidden bg-background-200">
                      <img
                        src={article.gallery[1]}
                        alt={article.title}
                        title={`${article.title} - 吃瓜爆料网`}
                        className="w-full aspect-[16/9] object-cover object-top"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Tags */}
            <div className="mt-7 pt-5 border-t border-background-100 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-400">相关标签：</span>
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/search?q=${encodeURIComponent(tag)}`}
                  className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            {/* Share + favorite */}
            <div className="mt-5 pt-5 border-t border-background-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-400 mr-1">分享到：</span>
                {shareItems.map((item) => (
                  <button
                    key={item.label}
                    type="button"
                    onClick={() => showToast("演示站点，分享链接暂不可用")}
                    aria-label={item.label}
                    className={`w-9 h-9 flex items-center justify-center rounded-full text-background-50 text-lg cursor-pointer hover:opacity-90 transition-opacity ${item.color}`}
                  >
                    <i className={item.icon} />
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={handleFavorite}
                className={`h-10 px-5 inline-flex items-center justify-center gap-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  favorited
                    ? "bg-accent-500 text-secondary-950"
                    : "border border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
                }`}
              >
                <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"} />
                {favorited ? "已收藏" : "收藏本文"}
              </button>
            </div>
          </article>

          {/* Sidebar: related */}
          <aside>
            <SectionTitle
              title="相关推荐"
              subtitle="基于分类与标签为你匹配"
              moreTo={`/category/${article.categorySlug}`}
              moreLabel="更多"
              icon="ri-links-line"
            />
            <div className="flex flex-col gap-4">
              {related.length > 0 ? (
                related.slice(0, 3).map((item) => (
                  <NewsCard key={item.id} article={item} variant="grid" />
                ))
              ) : (
                <p className="text-sm text-foreground-400">暂无相关推荐</p>
              )}
            </div>

            <div className="mt-6 rounded-lg bg-background-50 border border-background-200 p-4">
              <h3 className="font-bold text-foreground-950 flex items-center gap-2 mb-3">
                <i className="ri-list-unordered text-primary-500" />
                更多推荐
              </h3>
              {related.slice(3).map((item) => (
                <NewsCard key={item.id} article={item} variant="compact" />
              ))}
              {related.length <= 3 && <p className="text-sm text-foreground-400 py-2">暂无更多推荐</p>}
            </div>
          </aside>
        </div>
      </div>

      {/* Toast */}
      <div
        className={`fixed left-1/2 bottom-24 -translate-x-1/2 z-[80] px-5 py-3 rounded-full bg-secondary-900 text-background-50 text-sm transition-all duration-300 ${
          toast ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"
        }`}
      >
        {toast}
      </div>
    </Layout>
  );
}